import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import formatOfferInclusionDescription from 'lib/offer/formatOfferInclusionDescription'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'

const Container = styled.div`
  margin-top: ${rem(12)};
`

const LIST_FORMAT_OPTIONS: Intl.ListFormatOptions = { type: 'unit' }

interface Props {
  optionalBundles: Array<App.Property.OptionalBundle>
  selectedBundleIds: Array<string>
  onToggleBundle: (bundleId: string, event: React.ChangeEvent<HTMLInputElement>) => void
}

interface OptionalBundleRowProps {
  optionalBundle: App.Property.OptionalBundle
  checked: boolean
  onToggleBundle: Props['onToggleBundle']
}

function PropertyOptionalBundleRow({ optionalBundle, checked, onToggleBundle }: OptionalBundleRowProps) {
  const intl = useIntl()

  const onCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onToggleBundle(optionalBundle.bundleId, event)
  }, [onToggleBundle, optionalBundle.bundleId])

  const ticketDescriptions = useMemo(
    () => intl.formatList(
      optionalBundle.tickets.map(ticket => formatOfferInclusionDescription(ticket.inclusion.description, intl)),
      LIST_FORMAT_OPTIONS,
    ),
    [optionalBundle.tickets, intl],
  )

  return <CheckboxInput
    checked={checked}
    onChange={onCheckboxChange}
    size="medium"
  >
    {ticketDescriptions} <BodyText as="span" variant="medium" weight="bold" wrap="no-wrap">
      <FormatCurrency value={optionalBundle.totalPrice} format="roundedDollar" signDisplay="always" />
    </BodyText>
  </CheckboxInput>
}

/**
 * Customer-selectable bundles for a single rate. Ticking one adds its tickets to the checkout and
 * its `totalPrice` to the tile's displayed total — the rate price itself is hotel-only.
 */
function PropertyOptionalBundleOption({ optionalBundles, selectedBundleIds, onToggleBundle }: Props) {
  const intl = useIntl()

  if (!optionalBundles.length) return null

  return <Container>
    <Group direction="vertical" gap={12}>
      <Divider kind="primary" />
      <Group direction="vertical" gap={8}>
        <CaptionTextBlock variant="large" weight="bold" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'voSsewpDVES6C067x1oQ',
            defaultMessage: 'Enhance your journey',
            description: 'Heading above the customer-selectable add-on bundles on a hotel rate',
          })}
        </CaptionTextBlock>
        {optionalBundles.map(optionalBundle => <PropertyOptionalBundleRow
          key={optionalBundle.bundleId}
          optionalBundle={optionalBundle}
          checked={selectedBundleIds.includes(optionalBundle.bundleId)}
          onToggleBundle={onToggleBundle}
        />)}
      </Group>
    </Group>
  </Container>
}

export default PropertyOptionalBundleOption
